import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { Card, SubCard, SubCardTitle } from "../../common_components/Layout";
import { TitleAndText } from "../components/TitleAndText";
import { FormField, CommentField, DropdownQuestion, DropdownOption } from "../../common_components/Forms";
import CreateMissionHeaderAndTitleRow from "../components/CreateMissionHeaderAndTitleRow";
import CreationFooter from "../components/CreationFooter";
import { useAlert } from 'react-alert'
import StripePayment from "../components/StripePayment";
import { PrimaryButton } from "../../common_components/Buttons";
import IsValidEVMAddress from "../../../../../utility_components/IsValidAddress";
import FullPriceDisplay from "../../common_components/FullPriceDisplay";
import PriceGuarantees from "../../create_mission_components/components/PriceGuarantees";
import { CountryList } from "../components/CountryList";


// Api Calls
import PaymentAPI from "../../../api/api_calls/PaymentAPI";
import MissionPriceQuotesAPI from "../../../api/api_calls/MissionPriceQuotesAPI";
import CenteredLoadingIcon from "../../common_components/CenteredLoadingIcon";

function Payment(props) {

    const navigate = useNavigate();
    const alert = useAlert();
    const { missionId } = useParams()
    const [submitInProcess, setSubmitInProcess] = useState(false);
    const [price, setPrice] = useState(null);
    const [quantity, setQuantity] = useState(null)
    const [totalPrice, setTotalPrice] = useState(null)
    const [quantityType, setQuantityType] = useState(null)

    const [loadingPaymentTypes, setLoadingPaymentTypes] = useState(true);
    const [invoiceAcceptable, setInvoiceAcceptable] = useState(false);
    const [cryptoAcceptable, setCryptoAcceptable] = useState(false);
    const [creditCardAcceptable, setCreditCardAcceptable] = useState(false);

    const [paymentType, setPaymentType] = useState("creditCard")
    const [blockchain, setBlockchain] = useState("Polygon")
    const [companyCountry, setCompanyCountry] = useState("")
    const [companyAddress, setCompanyAddress] = useState("")

    const [cryptoPaymentSubmitting, setCryptoPaymentSubmitting] = useState(false);
    const [walletAddress, setWalletAddress] = useState("")

    const [invoicePaymentSubmitting, setInvoicePaymentSubmitting] = useState(false);

    const [missionType, loadingMissionType, nextPage, backPage] = [props.missionType, props.loadingMissionType, props.nextPage, props.backPage]


    useEffect(() => {
        // Enable tooltips
        async function getTotalPrice() {
            const result = await MissionPriceQuotesAPI.showTotal({ missionId, navigate });

            if (result.success) {
                setPrice(result.price);
                setQuantity(result.quantity);
                setQuantityType(result.quantity_type);
                setTotalPrice(result.total_price);
                // setMissionPriceQuoteId(result.mission_price_quote_id);
            } else {
                alert.show(result.error, { type: 'error' })
            }
        }

        async function getPaymentMethods() {
            const result = await PaymentAPI.getPaymentMethods({ missionId, navigate });
            if (result.success) {
                setInvoiceAcceptable(result.payment_methods.invoice);
                setCryptoAcceptable(result.payment_methods.crypto);
                setCreditCardAcceptable(result.payment_methods.credit_card);
                setPaymentType(result.payment_methods.invoice ? "invoice" : "creditCard");
                setLoadingPaymentTypes(false);
            } else {
                alert.show("Unable to load payment methods. Please try again later or contact info@despark.io", { type: 'error' })
                setLoadingPaymentTypes(true);
            }
        }

        // Get the total price and payment methods
        getTotalPrice();
        getPaymentMethods();
    }, []);


    function onClickBack() {
        navigate(backPage, { state: { missionId: missionId } })
    }

    function updatePaymentType(event) {
        setPaymentType(event.target.value)
    }

    function updateBlockchain(event) {
        setBlockchain(event.target.value)
    }

    function updateWalletAddress(event) {
        setWalletAddress(event.target.value)
    }

    function updateCompanyCountry(event) {
        setCompanyCountry(event.target.value)
    }

    function updateCompanyAddress(event) {
        setCompanyAddress(event.target.value)
    }

    function submitCryptoPayment() {
        if (!walletAddress || !IsValidEVMAddress(walletAddress)) { alert.show("Please enter a valid EVM wallet address", { type: 'error' }); return; }

        setCryptoPaymentSubmitting(true);

        async function submitCryptoPayment() {
            const result = await PaymentAPI.createCryptoPayment({ missionId, walletAddress, blockchain, companyCountry, companyAddress, navigate });

            if (result.success) {
                setCryptoPaymentSubmitting(false);
                navigate("/createmission/" + missionId + "/cryptoconfirmation")
            } else {
                alert.show(result.error, { type: 'error' })
                setCryptoPaymentSubmitting(false);
            }
        }
        submitCryptoPayment()
    }

    function submitInvoicePayment() {
        setInvoicePaymentSubmitting(true);
        async function submitInvoicePayment() {
            const result = await PaymentAPI.createInvoicePayment({ missionId, navigate });

            if (result.success) {
                setInvoicePaymentSubmitting(false);
                navigate("/createmission/" + missionId + "/invoiceconfirmation")
            } else {
                alert.show(result.error, { type: 'error' })
                setInvoicePaymentSubmitting(false);
            }
        }
        submitInvoicePayment()
    }



    return (
        <div className="create-mission-primary-content-container">
            {/* <div className="payment-details-container"> */}
            <div className="create-mission-header-row">
                <div className="create-mission-header-text-div">
                    <div className="create-mission-title">
                        Payment
                    </div>
                    <div className="create-mission-subtitle">
                        Last step - set up your payment profile. You will <span className="bold-text">NOT</span> be charged until the project is complete.
                    </div>
                </div>
            </div>
            <div className="full-price-and-info-container">
                <FullPriceDisplay price={price} quantity={quantity} totalPrice={totalPrice} quantityType={quantityType} showTaxNote={true} />
                <PriceGuarantees />
            </div>
            <div className="payment-methods-container">
                {loadingPaymentTypes ?
                    <CenteredLoadingIcon />
                    :
                    <div className="payment-methods-container-inner">
                        <PaymentMethodDropdown
                            updatePaymentType={updatePaymentType}
                            invoiceAcceptable={invoiceAcceptable}
                            cryptoAcceptable={cryptoAcceptable}
                            creditCardAcceptable={creditCardAcceptable}
                        />
                        {paymentType === "creditCard" ?
                            <StripePaymentContainer />
                            :
                            paymentType === "crypto" ?
                                <CryptoPaymentContainer
                                    updateBlockchain={updateBlockchain}
                                    updateWalletAddress={updateWalletAddress}
                                    updateCompanyCountry={updateCompanyCountry}
                                    updateCompanyAddress={updateCompanyAddress}
                                    submitCryptoPayment={submitCryptoPayment}
                                    cryptoPaymentSubmitting={cryptoPaymentSubmitting}
                                />
                                : paymentType === "invoice" ?
                                    <InvoicePaymentContainer
                                        submitInvoicePayment={submitInvoicePayment}
                                        invoicePaymentSubmitting={invoicePaymentSubmitting}
                                    />
                                    : null
                        }
                    </div>
                }
            </div>
            <CreationFooter onClickBack={onClickBack} />
            {/* </div> */}
        </div>

    )
}

function PaymentMethodDropdown({ updatePaymentType, invoiceAcceptable, cryptoAcceptable, creditCardAcceptable }) {
    const defaultPaymentType = invoiceAcceptable ? "invoice" : "creditCard";

    return (
        <div className="payment-dropdown-container">
            <DropdownQuestion
                name="paymentType"
                id="paymentType"
                onChange={updatePaymentType}
                defaultValue={defaultPaymentType}
                questionText="Payment method"
            >
                {invoiceAcceptable && <DropdownOption value="invoice" key="invoice" >Invoice</DropdownOption>}
                <DropdownOption value="creditCard" key="cc" >Credit Card</DropdownOption>
                <DropdownOption value="crypto" key="crypto" >Cryptocurrency - USDC</DropdownOption>
            </DropdownQuestion>
        </div>
    )
}

function StripePaymentContainer() {
    return (
        <div className="stripe-payment-container">
            <div className="label-mimic-text">Card Details</div>
            <StripePayment />
        </div>
    )
}

function CryptoPaymentContainer({ updateBlockchain, updateWalletAddress, updateCompanyCountry, updateCompanyAddress, submitCryptoPayment, cryptoPaymentSubmitting }) {
    return (
        <SubCard style={{ padding: "16px" }}>
            <SubCardTitle>Paying with Crypto</SubCardTitle>
            <div className="white-text"> We accept crypto payments in USDC on Ethereum and Polygon. Paying with crypto may slightly delay your project launch. We will reach out to via email to verify your email and wallet. </div>
            <DropdownQuestion name="blockchain" id="blockchain" onChange={updateBlockchain} defaultValue="polygon" questionText="Blockchain" >
                <DropdownOption value="Polygon" key="polygon" >Polygon</DropdownOption>
                <DropdownOption value="Ethereum" key="ethereum" >Ethereum</DropdownOption>
            </DropdownQuestion>
            <FormField name="walletAddress" id="walletAddress" onChange={updateWalletAddress} label="Wallet Address" placeholder="Enter the wallet address your company uses to send USDC" />
            <DropdownQuestion name="companyCountry" id="companyCountry" onChange={updateCompanyCountry} placeholder="Select Country" questionText="Company Country" >
                {CountryList().map((country) => {
                    return <DropdownOption value={country} key={country} >{country}</DropdownOption>
                }
                )}
            </DropdownQuestion>
            <FormField name="companyAddress" id="companyAddress" onChange={updateCompanyAddress} label="Company Address (for taxes)" placeholder="What is the address of your company? " />

            <PrimaryButton onClick={submitCryptoPayment} text="Pay with Crypto" loading={cryptoPaymentSubmitting} />
        </SubCard>
    )
}

function InvoicePaymentContainer({ submitInvoicePayment, invoicePaymentSubmitting }) {
    return (
        <SubCard style={{ padding: "16px" }}>
            <SubCardTitle>Invoice Payments</SubCardTitle>
            <div className="white-text"> Your company is approved for invoice payments. We will charge you for the project upon completion. The exact method is based on agreement with your company. Please contact us for exact details. </div>
            <PrimaryButton onClick={submitInvoicePayment} text="Launch Project" loading={invoicePaymentSubmitting} />
        </SubCard>
    )
}

export default Payment;